// Import from the source module rather than the `@/services/api` barrel: this
// file is pulled into the api-barrel <-> services/auth import cycle (via
// auth/hooks), and the barrel exports `hydraUiApi` after `servicesApi`, so going
// through the barrel can observe it before it's initialized.
import { hydraUiApi } from '@/services/api/hydra-ui-api';
import { crossAppCache } from '@/utils/cross-app-cache';
import type { RawAccount, RawUser, Workspace } from './types';

/**
 * Account reads for the auth/session bootstrap, routed through
 * `hydraUiApi.accounts.*` while preserving the cross-app cache that the
 * `@customerio/hydra-authentication` session singleton seeds on boot and that
 * other embedded apps (Journeys UI, Pipelines) read.
 *
 * The current-account fetch hits the `/v1/accounts/:account_id` route with the
 * literal `account_id` "current"; the backend resolves it to the session's
 * account.
 */

const ACCOUNT_CACHE_TTL = 10000;

export interface AccountResponse {
  account: RawAccount;
  environments: Workspace[];
  user: RawUser;
  session_expires_at: number;
}

// The generated `accountResponse` is the same backend payload as the old
// ts-rest contract, so narrow it back to the hand-written account shape the
// auth mappers consume.
function toAccountResponse(data: Awaited<ReturnType<typeof hydraUiApi.accounts.get>>): AccountResponse {
  return data as unknown as AccountResponse;
}

/** Fetch the session's current account, seeding the cross-app cache like the old `servicesApi.getAccount()`. */
export async function fetchCurrentAccount(): Promise<AccountResponse> {
  const cached = crossAppCache.getValue('/accounts/current');
  if (cached != null) {
    return cached as AccountResponse;
  }

  const response = toAccountResponse(await hydraUiApi.accounts.get({ params: { path: { account_id: 'current' } } }));

  if (response.account?.id != null) {
    crossAppCache.setValue({ key: '/accounts/current', value: response, ttl: ACCOUNT_CACHE_TTL, cacheOnlyOnce: true });
    crossAppCache.setValue({
      key: `/accounts/${response.account.id}`,
      value: response,
      ttl: ACCOUNT_CACHE_TTL,
      cacheOnlyOnce: true,
    });
  }

  return response;
}

/** Fetch an account by ID, mirroring the cross-app cache behavior of the old `servicesApi.getAccountById()`. */
export async function fetchAccountById(accountId: number): Promise<AccountResponse> {
  const cached = crossAppCache.getValue(`/accounts/${accountId}`);
  if (cached != null) {
    return cached as AccountResponse;
  }

  const response = toAccountResponse(
    await hydraUiApi.accounts.get({ params: { path: { account_id: String(accountId) } } }),
  );

  if (response.account?.id != null) {
    crossAppCache.setValue({
      key: `/accounts/${accountId}`,
      value: response,
      ttl: ACCOUNT_CACHE_TTL,
      cacheOnlyOnce: true,
    });
  }

  return response;
}

/** Fetch an account by environment ID, mirroring the old `servicesApi.getAccountByEnvironmentId()`. */
export async function fetchAccountByEnvironment(environmentId: number): Promise<AccountResponse> {
  const cached = crossAppCache.getValue(`/account_by_environment/${environmentId}`);
  if (cached != null) {
    return cached as AccountResponse;
  }

  const response = toAccountResponse(
    await hydraUiApi.accounts.getAccountByEnvironment({ params: { path: { environment_id: String(environmentId) } } }),
  );

  if (response.account?.id != null) {
    crossAppCache.setValue({
      key: `/account_by_environment/${environmentId}`,
      value: response,
      ttl: ACCOUNT_CACHE_TTL,
      cacheOnlyOnce: true,
    });
    crossAppCache.setValue({
      key: `/accounts/${response.account.id}`,
      value: response,
      ttl: ACCOUNT_CACHE_TTL,
      cacheOnlyOnce: true,
    });
  }

  return response;
}
